import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/auth/AuthProvider";
import { generateInvoicePdf } from "@/utils/reports.functions";
import { downloadPdfFromBase64 } from "@/lib/report-export";
import { PdfCanvasPreview } from "@/components/settings/PdfCanvasPreview";
import { ScopePhotosUploader, type ScopePhoto } from "@/components/ScopePhotosUploader";
import { hydrateScopePhotos, serializeScopePhotos } from "@/lib/scope-photos";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  Trash2,
  Plus,
  DollarSign,
  Calendar,
  Lock,
  Unlock,
  FileDown,
  Loader2,
  Send,
  Pencil,
  Save,
  X,
  Eye,
} from "lucide-react";
import { sendInvoiceEmail, previewInvoiceEmail } from "@/utils/invoices.functions";
import { EmailSendHistoryCard } from "@/components/estimates/EmailSendHistoryCard";
import { InvoiceClientViewsCard } from "@/components/invoices/InvoiceClientViewsCard";

export const Route = createFileRoute("/_app/invoices/$invoiceId")({
  component: InvoiceDetailPage,
});

type Status = "draft" | "sent" | "paid" | "partial" | "overdue" | "cancelled";
type Method = "cash" | "check" | "card" | "bank_transfer" | "other";

type Invoice = {
  id: string;
  number: string;
  title: string;
  description: string | null;
  status: Status;
  issue_date: string;
  due_date: string | null;
  paid_date: string | null;
  currency: string;
  tax_percent: number;
  retainage_percent: number;
  subtotal: number;
  tax_amount: number;
  retainage_amount: number;
  total: number;
  amount_paid: number;
  notes: string | null;
  client_id: string;
  locked_at: string | null;
  client_snapshot_name: string | null;
  client_snapshot_email: string | null;
  client_snapshot_address: string | null;
  contract_snapshot_number: string | null;
  contract_snapshot_title: string | null;
  is_retainage_release: boolean;
  retainage_released_at: string | null;
  retainage_released_invoice_id: string | null;
  source_invoice_id: string | null;
  clients: { name: string; email: string | null; language: string | null } | null;
  company_id: string;
  scope_photos: unknown;
};

type Item = {
  id: string;
  description: string;
  quantity: number;
  unit_price: number;
  total: number;
};

type Payment = {
  id: string;
  amount: number;
  payment_date: string;
  method: Method;
  reference: string | null;
  notes: string | null;
};

const variant: Record<Status, "default" | "secondary" | "destructive" | "outline"> = {
  draft: "secondary",
  sent: "outline",
  paid: "default",
  partial: "outline",
  overdue: "destructive",
  cancelled: "secondary",
};

function InvoiceDetailPage() {
  const { t } = useTranslation();
  const { invoiceId } = Route.useParams();
  const navigate = useNavigate();
  const { profile, company, hasRole } = useAuth();
  const isAdmin = hasRole("admin");

  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [contractSummary, setContractSummary] = useState<{
    amount: number;
    invoicedTotal: number;
    paidTotal: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<Status | null>(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewBase64, setPdfPreviewBase64] = useState<string | null>(null);
  const [pdfPreviewFilename, setPdfPreviewFilename] = useState<string>("");
  const generateInvoicePdfFn = useServerFn(generateInvoicePdf);
  const sendInvoiceEmailFn = useServerFn(sendInvoiceEmail);
  const previewInvoiceEmailFn = useServerFn(previewInvoiceEmail);

  const [sendOpen, setSendOpen] = useState(false);
  const [sendEmail, setSendEmail] = useState("");
  const [sendMessage, setSendMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
  const [emailPreviewUrl, setEmailPreviewUrl] = useState<string | null>(null);
  const [emailPreviewMeta, setEmailPreviewMeta] = useState<{
    subject: string;
    from: string;
    recipient: string;
  } | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Paid-receipt send flow (offered after a payment fully settles the invoice)
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptEmail, setReceiptEmail] = useState("");
  const [receiptMessage, setReceiptMessage] = useState("");
  const [sendingReceipt, setSendingReceipt] = useState(false);

  useEffect(() => {
    return () => {
      if (emailPreviewUrl) URL.revokeObjectURL(emailPreviewUrl);
    };
  }, [emailPreviewUrl]);

  const openEmailPreview = async () => {
    if (!invoice) return;
    setLoadingPreview(true);
    try {
      const appOrigin = typeof window !== "undefined" ? window.location.origin : "";
      const res = await previewInvoiceEmailFn({
        data: {
          invoiceId: invoice.id,
          message: sendMessage.trim() || undefined,
          appOrigin,
          language: invoice.clients?.language || undefined,
        },
      });
      const blob = new Blob([res.html], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      if (emailPreviewUrl) URL.revokeObjectURL(emailPreviewUrl);
      setEmailPreviewUrl(url);
      setEmailPreviewMeta({
        subject: res.subject,
        from: res.from,
        recipient: sendEmail.trim() || res.recipient,
      });
      setEmailPreviewOpen(true);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setLoadingPreview(false);
    }
  };

  const openSendDialog = () => {
    setSendEmail(invoice?.client_snapshot_email ?? invoice?.clients?.email ?? "");
    setSendMessage("");
    setSendOpen(true);
  };

  // Shared send routine for both the invoice notification and the paid receipt.
  // Generates the PDF in the client's language, uploads it, and emails the client.
  const doSend = async (
    kind: "invoice" | "receipt",
    email: string,
    message: string,
  ): Promise<boolean> => {
    if (!invoice) return false;
    const language = invoice.clients?.language || undefined;

    // 1) Generate the PDF and upload to public storage so the client can
    //    download it from the email.
    let pdfPublicUrl: string | undefined;
    try {
      const { pdfBase64, filename } = await generateInvoicePdfFn({
        data: { invoiceId: invoice.id, language },
      });
      const bin = atob(pdfBase64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const safeName = (filename || `invoice-${invoice.number}.pdf`).replace(
        /[^a-zA-Z0-9._-]+/g,
        "-",
      );
      const storagePath = `invoices/${invoice.id}/${Date.now()}-${safeName}`;
      const { error: upErr } = await supabase.storage
        .from("document-pdfs")
        .upload(storagePath, new Blob([bytes], { type: "application/pdf" }), {
          contentType: "application/pdf",
          upsert: true,
        });
      if (upErr) throw upErr;
      const { data: pub } = supabase.storage.from("document-pdfs").getPublicUrl(storagePath);
      pdfPublicUrl = pub?.publicUrl;
    } catch (pdfErr) {
      console.error("Invoice PDF generation/upload failed", pdfErr);
    }

    const appOrigin = typeof window !== "undefined" ? window.location.origin : "";
    const result = await sendInvoiceEmailFn({
      data: {
        invoiceId: invoice.id,
        recipientEmail: email,
        message: message.trim() || undefined,
        appOrigin,
        pdfUrl: pdfPublicUrl,
        language,
        kind,
      },
    });
    return !!result?.ok;
  };

  const sendInvoice = async () => {
    if (!invoice) return;
    const email = sendEmail.trim();
    if (!email) {
      toast.error(
        t("pages.invoices.detail.sendEmailRequired", {
          defaultValue: "Recipient email is required",
        }),
      );
      return;
    }
    setSending(true);
    try {
      const ok = await doSend("invoice", email, sendMessage);
      if (ok) {
        toast.success(t("pages.invoices.detail.sendSuccess", { defaultValue: "Invoice sent" }));
        setSendOpen(false);
      } else {
        toast.error(
          t("pages.invoices.detail.sendFailed", { defaultValue: "Failed to send invoice" }),
        );
      }
    } catch (e) {
      toast.error(
        t("pages.invoices.detail.sendFailed", { defaultValue: "Failed to send invoice" }),
        {
          description: e instanceof Error ? e.message : String(e),
        },
      );
    } finally {
      setSending(false);
    }
  };

  const sendReceipt = async () => {
    if (!invoice) return;
    const email = receiptEmail.trim();
    if (!email) {
      toast.error(
        t("pages.invoices.detail.sendEmailRequired", {
          defaultValue: "Recipient email is required",
        }),
      );
      return;
    }
    setSendingReceipt(true);
    try {
      const ok = await doSend("receipt", email, receiptMessage);
      if (ok) {
        toast.success(
          t("pages.invoices.detail.receiptSentToast", {
            defaultValue: "Paid receipt sent to the client",
          }),
        );
        setReceiptOpen(false);
      } else {
        toast.error(
          t("pages.invoices.detail.receiptSendFailed", {
            defaultValue: "Failed to send receipt",
          }),
        );
      }
    } catch (e) {
      toast.error(
        t("pages.invoices.detail.receiptSendFailed", { defaultValue: "Failed to send receipt" }),
        {
          description: e instanceof Error ? e.message : String(e),
        },
      );
    } finally {
      setSendingReceipt(false);
    }
  };

  const openPdfPreview = async () => {
    if (!invoice) return;
    setDownloadingPdf(true);
    try {
      const { pdfBase64, filename } = await generateInvoicePdfFn({
        data: { invoiceId: invoice.id },
      });
      setPdfPreviewBase64(pdfBase64);
      setPdfPreviewFilename(filename || `fatura-${invoice.number}.pdf`);
      setPdfPreviewOpen(true);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("pages.invoices.detail.pdfFailedToast"));
    } finally {
      setDownloadingPdf(false);
    }
  };

  const confirmDownloadPdf = () => {
    if (!pdfPreviewBase64) return;
    downloadPdfFromBase64(
      pdfPreviewFilename || `fatura-${invoice?.number ?? "invoice"}.pdf`,
      pdfPreviewBase64,
    );
    toast.success(t("pages.invoices.detail.pdfDownloadedToast"));
  };

  const closePdfPreview = () => {
    setPdfPreviewOpen(false);
    setPdfPreviewBase64(null);
  };

  const [confirmRelease, setConfirmRelease] = useState(false);
  const [releasing, setReleasing] = useState(false);

  const [payOpen, setPayOpen] = useState(false);
  const [payAmount, setPayAmount] = useState<number>(0);
  const [payMethod, setPayMethod] = useState<Method>("bank_transfer");
  const [payReference, setPayReference] = useState("");
  const [payNotes, setPayNotes] = useState("");
  const [payDate, setPayDate] = useState(new Date().toISOString().slice(0, 10));
  const [recording, setRecording] = useState(false);

  // ===== Edit mode (only available while invoice is in draft) =====
  const [editing, setEditing] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editIssueDate, setEditIssueDate] = useState("");
  const [editDueDate, setEditDueDate] = useState("");
  const [editTaxPercent, setEditTaxPercent] = useState<number>(0);
  const [editRetainagePercent, setEditRetainagePercent] = useState<number>(0);
  const [editItems, setEditItems] = useState<
    Array<{
      id: string;
      description: string;
      quantity: number;
      unit_price: number;
      isNew?: boolean;
      toDelete?: boolean;
    }>
  >([]);
  const [editScopePhotos, setEditScopePhotos] = useState<ScopePhoto[]>([]);

  const startEditing = () => {
    if (!invoice) return;
    setEditTitle(invoice.title ?? "");
    setEditDescription(invoice.description ?? "");
    setEditNotes(invoice.notes ?? "");
    setEditIssueDate(invoice.issue_date?.slice(0, 10) ?? "");
    setEditDueDate(invoice.due_date?.slice(0, 10) ?? "");
    setEditTaxPercent(Number(invoice.tax_percent) || 0);
    setEditRetainagePercent(Number(invoice.retainage_percent) || 0);
    setEditItems(
      items.map((it) => ({
        id: it.id,
        description: it.description,
        quantity: Number(it.quantity) || 0,
        unit_price: Number(it.unit_price) || 0,
      })),
    );
    setEditing(true);
    void hydrateScopePhotos("invoice-scope-photos", invoice.scope_photos).then(setEditScopePhotos);
  };

  const cancelEditing = () => {
    setEditing(false);
  };

  const addEditItem = () => {
    setEditItems((prev) => [
      ...prev,
      {
        id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        description: "",
        quantity: 1,
        unit_price: 0,
        isNew: true,
      },
    ]);
  };

  const updateEditItem = (
    id: string,
    patch: Partial<{ description: string; quantity: number; unit_price: number }>,
  ) => {
    setEditItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  };

  const removeEditItem = (id: string) => {
    setEditItems((prev) =>
      prev
        .map((it) => (it.id === id ? (it.isNew ? null : { ...it, toDelete: true }) : it))
        .filter((it): it is NonNullable<typeof it> => it !== null),
    );
  };

  const saveEditing = async () => {
    if (!invoice) return;
    if (!editTitle.trim()) {
      toast.error(t("pages.invoices.detail.titleRequired", { defaultValue: "Title is required" }));
      return;
    }
    setSavingEdit(true);
    try {
      // 1) Update invoice fields
      const { error: invErr } = await supabase
        .from("invoices")
        .update({
          title: editTitle.trim(),
          description: editDescription.trim() || null,
          notes: editNotes.trim() || null,
          issue_date: editIssueDate || invoice.issue_date,
          due_date: editDueDate || null,
          tax_percent: Number(editTaxPercent) || 0,
          retainage_percent: Number(editRetainagePercent) || 0,
          scope_photos: serializeScopePhotos(editScopePhotos),
        })
        .eq("id", invoice.id);
      if (invErr) throw invErr;

      // 2) Apply item changes (deletes, updates, inserts)
      const toDelete = editItems.filter((it) => it.toDelete && !it.isNew);
      const toUpdate = editItems.filter((it) => !it.toDelete && !it.isNew);
      const toInsert = editItems.filter((it) => it.isNew && !it.toDelete);

      if (toDelete.length > 0) {
        const { error: delErr } = await supabase
          .from("invoice_items")
          .delete()
          .in(
            "id",
            toDelete.map((it) => it.id),
          );
        if (delErr) throw delErr;
      }

      for (let i = 0; i < toUpdate.length; i++) {
        const it = toUpdate[i];
        const { error: upErr } = await supabase
          .from("invoice_items")
          .update({
            description: it.description,
            quantity: Number(it.quantity) || 0,
            unit_price: Number(it.unit_price) || 0,
            sort_order: i,
          })
          .eq("id", it.id);
        if (upErr) throw upErr;
      }

      if (toInsert.length > 0) {
        const offset = toUpdate.length;
        const { error: insErr } = await supabase.from("invoice_items").insert(
          toInsert.map((it, i) => ({
            invoice_id: invoice.id,
            description: it.description,
            quantity: Number(it.quantity) || 0,
            unit_price: Number(it.unit_price) || 0,
            sort_order: offset + i,
          })),
        );
        if (insErr) throw insErr;
      }

      toast.success(t("pages.invoices.detail.editSavedToast", { defaultValue: "Invoice updated" }));
      setEditing(false);
      await load();
    } catch (e) {
      toast.error(
        t("pages.invoices.detail.editFailedToast", { defaultValue: "Failed to save changes" }),
        {
          description: e instanceof Error ? e.message : String(e),
        },
      );
    } finally {
      setSavingEdit(false);
    }
  };

  const load = async () => {
    setLoading(true);
    const [{ data: inv, error: e1 }, { data: its }, { data: pays }] = await Promise.all([
      supabase
        .from("invoices")
        .select("*, clients(name, email, language)")
        .eq("id", invoiceId)
        .maybeSingle(),
      supabase
        .from("invoice_items")
        .select("id, description, quantity, unit_price, total")
        .eq("invoice_id", invoiceId)
        .order("sort_order"),
      supabase
        .from("invoice_payments")
        .select("id, amount, payment_date, method, reference, notes")
        .eq("invoice_id", invoiceId)
        .order("payment_date", { ascending: false }),
    ]);
    if (e1) toast.error(t("pages.invoices.detail.loadFailedToast"), { description: e1.message });
    setInvoice((inv ?? null) as Invoice | null);
    setItems((its ?? []) as Item[]);
    setPayments((pays ?? []) as Payment[]);
    if (inv) {
      const balance = Math.max(Number(inv.total) - Number(inv.amount_paid), 0);
      setPayAmount(balance);
      if (inv.contract_id) {
        const [{ data: c }, { data: invs }] = await Promise.all([
          supabase.from("contracts").select("amount").eq("id", inv.contract_id).maybeSingle(),
          supabase
            .from("invoices")
            .select("total, amount_paid, status")
            .eq("contract_id", inv.contract_id),
        ]);
        if (c) {
          const rows = (invs ?? []) as Array<{
            total: number;
            amount_paid: number | null;
            status: string;
          }>;
          const activeRows = rows.filter((r) => r.status !== "cancelled");
          const invoicedTotal = activeRows.reduce((acc, r) => {
            const total = Number(r.total ?? 0);
            const paid = Number(r.amount_paid ?? 0);
            // Para faturas parciais, considera apenas a parte efetivamente paga como "faturado consumido"
            return acc + (r.status === "partial" ? Math.min(paid, total) : total);
          }, 0);
          const paidTotal = activeRows.reduce((acc, r) => acc + Number(r.amount_paid ?? 0), 0);
          setContractSummary({ amount: Number(c.amount ?? 0), invoicedTotal, paidTotal });
        } else {
          setContractSummary(null);
        }
      } else {
        setContractSummary(null);
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);

  const updateStatus = async (status: Status) => {
    if (!invoice) return;
    const { error } = await supabase.from("invoices").update({ status }).eq("id", invoice.id);
    if (error)
      toast.error(t("pages.invoices.detail.statusFailedToast"), { description: error.message });
    else {
      toast.success(
        t("pages.invoices.detail.statusUpdatedToast", { status: t(`statuses.${status}`) }),
      );
      load();
    }
  };

  const onStatusChange = (status: Status) => {
    if (!invoice) return;
    // If transitioning OUT of draft for the first time → confirmation required (locks the invoice)
    if (invoice.status === "draft" && status !== "draft") {
      setPendingStatus(status);
      return;
    }
    updateStatus(status);
  };

  const recordPayment = async () => {
    if (!invoice || !company?.id || !profile?.id) return;
    if (!payAmount || payAmount <= 0) return toast.error(t("pages.invoices.detail.amountPositive"));

    // Will this payment fully settle the invoice? (trigger flips status → "paid")
    const total = Number(invoice.total) || 0;
    const alreadyPaid = Number(invoice.amount_paid) || 0;
    const settlesInvoice =
      total > 0 &&
      alreadyPaid + payAmount >= total - 0.005 &&
      invoice.status !== "paid" &&
      invoice.status !== "draft" &&
      invoice.status !== "cancelled";
    const clientEmail = invoice.client_snapshot_email ?? invoice.clients?.email ?? "";

    setRecording(true);
    const { error } = await supabase.from("invoice_payments").insert({
      invoice_id: invoice.id,
      company_id: company.id,
      recorded_by: profile.id,
      amount: payAmount,
      payment_date: payDate,
      method: payMethod,
      reference: payReference.trim() || null,
      notes: payNotes.trim() || null,
    });
    setRecording(false);
    if (error) {
      toast.error(t("pages.invoices.detail.paymentFailed"), { description: error.message });
    } else {
      toast.success(t("pages.invoices.detail.paymentRecorded"));
      setPayOpen(false);
      setPayReference("");
      setPayNotes("");
      await load();
      // Once paid, offer to send the paid receipt (with PAID stamp) to the client.
      if (settlesInvoice) {
        setReceiptEmail(clientEmail);
        setReceiptMessage("");
        setReceiptOpen(true);
      }
    }
  };

  const deletePayment = async (id: string) => {
    const { error } = await supabase.from("invoice_payments").delete().eq("id", id);
    if (error)
      toast.error(t("pages.invoices.detail.deletePaymentFailedToast"), {
        description: error.message,
      });
    else {
      toast.success(t("pages.invoices.detail.paymentRemovedToast"));
      load();
    }
  };

  const onDeleteInvoice = async () => {
    if (!invoice) return;
    // Faturas em draft ou cancelled podem ser excluídas. Outras (enviadas/pagas) só podem ser canceladas
    // por integridade contábil (regra do banco enforce_invoice_immutability).
    if (invoice.status === "draft" || invoice.status === "cancelled") {
      const { error } = await supabase.from("invoices").delete().eq("id", invoice.id);
      if (error)
        toast.error(t("pages.invoices.detail.deleteFailedToast"), { description: error.message });
      else {
        toast.success(t("pages.invoices.detail.invoiceDeletedToast"));
        navigate({ to: "/invoices" });
      }
    } else {
      const { error } = await supabase
        .from("invoices")
        .update({ status: "cancelled" })
        .eq("id", invoice.id);
      if (error) {
        toast.error(t("pages.invoices.detail.deleteFailedToast"), { description: error.message });
      } else {
        toast.success(t("pages.invoices.detail.cancelledToast"), {
          description: t("pages.invoices.detail.cancelledToastDesc"),
        });
        navigate({ to: "/invoices" });
      }
    }
  };

  const releaseRetainage = async () => {
    if (!invoice || !company?.id || !profile?.id) return;
    if (Number(invoice.retainage_amount) <= 0) {
      return toast.error(t("pages.invoices.detail.noRetainageError"));
    }
    if (invoice.retainage_released_invoice_id) {
      return toast.error(t("pages.invoices.detail.alreadyReleasedError"));
    }
    setReleasing(true);
    try {
      const amount = Number(invoice.retainage_amount);
      // Create a new draft invoice for the retainage amount, snapshotting client + source ref
      const { data: newInv, error: insErr } = await supabase
        .from("invoices")
        .insert({
          company_id: company.id,
          client_id: invoice.client_id,
          contract_id: null,
          estimate_id: null,
          created_by: profile.id,
          title: t("pages.invoices.detail.releaseRetainageTitle", { number: invoice.number }),
          description: t("pages.invoices.detail.releaseInvoiceDescription", {
            number: invoice.number,
          }),
          status: "draft",
          tax_percent: 0,
          retainage_percent: 0,
          currency: invoice.currency,
          number: "",
          is_retainage_release: true,
          source_invoice_id: invoice.id,
          client_snapshot_name: invoice.client_snapshot_name,
          client_snapshot_email: invoice.client_snapshot_email,
          client_snapshot_address: invoice.client_snapshot_address,
          contract_snapshot_number: invoice.contract_snapshot_number,
          contract_snapshot_title: invoice.contract_snapshot_title,
        })
        .select("id, number")
        .single();
      if (insErr || !newInv)
        throw insErr ?? new Error(t("pages.invoices.detail.releaseInvoiceCreateError"));

      const { error: itErr } = await supabase.from("invoice_items").insert({
        invoice_id: newInv.id,
        description: t("pages.invoices.detail.releaseItemDescription", { number: invoice.number }),
        quantity: 1,
        unit_price: amount,
        sort_order: 0,
      });
      if (itErr) throw itErr;

      // Mark the source invoice as released (allowed by trigger whitelist)
      const { error: upErr } = await supabase
        .from("invoices")
        .update({
          retainage_released_at: new Date().toISOString(),
          retainage_released_invoice_id: newInv.id,
        })
        .eq("id", invoice.id);
      if (upErr) throw upErr;

      toast.success(t("pages.invoices.detail.releaseSuccessToast", { number: newInv.number }));
      setConfirmRelease(false);
      navigate({ to: "/invoices/$invoiceId", params: { invoiceId: newInv.id } });
    } catch (e) {
      const msg = e instanceof Error ? e.message : t("pages.invoices.detail.releaseFailedDefault");
      toast.error(t("pages.invoices.detail.releaseFailedToast"), { description: msg });
    } finally {
      setReleasing(false);
    }
  };

  const fm = (v: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: invoice?.currency || "USD",
    }).format(Number(v) || 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-20">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }
  if (!invoice) {
    return (
      <div className="rounded-lg border border-border bg-card p-12 text-center">
        <p className="font-medium">{t("pages.invoices.detail.invoiceNotFound")}</p>
        <Link to="/invoices" className="mt-2 inline-block text-sm text-primary hover:underline">
          {t("pages.invoices.detail.backToInvoices")}
        </Link>
      </div>
    );
  }

  const balance = Math.max(Number(invoice.total) - Number(invoice.amount_paid), 0);
  const isLocked = invoice.status !== "draft";
  // Snapshot fallback: prefer frozen snapshot once locked, otherwise live client data
  const displayClientName = invoice.client_snapshot_name ?? invoice.clients?.name ?? "—";
  const displayClientEmail = invoice.client_snapshot_email ?? invoice.clients?.email ?? undefined;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate({ to: "/invoices" })}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs text-muted-foreground">{invoice.number}</span>
              <Badge variant={variant[invoice.status]} className="capitalize">
                {t(`statuses.${invoice.status}`)}
              </Badge>
              {isLocked && (
                <Badge variant="outline" className="gap-1">
                  <Lock className="h-3 w-3" />
                  {t("pages.invoices.detail.lockedBadge")}
                </Badge>
              )}
            </div>
            <h1 className="text-2xl font-semibold tracking-tight">{invoice.title}</h1>
            {invoice.locked_at && (
              <p className="mt-1 text-xs text-muted-foreground">
                {t("pages.invoices.detail.lockedOnPrefix", {
                  date: new Date(invoice.locked_at).toLocaleString(),
                })}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {balance > 0 && invoice.status !== "cancelled" && (
            <Button onClick={() => setPayOpen(true)} className="gap-2">
              <DollarSign className="h-4 w-4" />
              {t("pages.invoices.detail.recordPaymentBtn")}
            </Button>
          )}
          {isAdmin &&
            isLocked &&
            Number(invoice.retainage_amount) > 0 &&
            !invoice.is_retainage_release &&
            !invoice.retainage_released_invoice_id &&
            invoice.status !== "cancelled" && (
              <Button variant="outline" onClick={() => setConfirmRelease(true)} className="gap-2">
                <Unlock className="h-4 w-4" />
                {t("pages.invoices.detail.releaseRetainageBtn")}
              </Button>
            )}
          {invoice.retainage_released_invoice_id && (
            <Button
              variant="ghost"
              onClick={() =>
                navigate({
                  to: "/invoices/$invoiceId",
                  params: { invoiceId: invoice.retainage_released_invoice_id! },
                })
              }
              className="gap-2"
            >
              <Unlock className="h-4 w-4" />
              {t("pages.invoices.detail.viewReleaseInvoiceBtn")}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={openPdfPreview}
            disabled={downloadingPdf}
            className="gap-2"
          >
            <FileDown className="h-4 w-4" />
            {downloadingPdf
              ? t("pages.invoices.detail.pdfGenerating")
              : t("pages.invoices.detail.pdfBtn")}
          </Button>
          {invoice.status !== "cancelled" && (
            <Button variant="outline" onClick={openSendDialog} className="gap-2">
              <Send className="h-4 w-4" />
              {t("pages.invoices.detail.sendBtn", { defaultValue: "Send to client" })}
            </Button>
          )}
          <Select value={invoice.status} onValueChange={(v) => onStatusChange(v as Status)}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="draft" disabled={isLocked}>
                {t("statuses.draft")}
              </SelectItem>
              <SelectItem value="sent">{t("statuses.sent")}</SelectItem>
              <SelectItem value="partial">{t("statuses.partial")}</SelectItem>
              <SelectItem value="paid">{t("statuses.paid")}</SelectItem>
              <SelectItem value="overdue">{t("statuses.overdue")}</SelectItem>
              <SelectItem value="cancelled">{t("statuses.cancelled")}</SelectItem>
            </SelectContent>
          </Select>
          {!isLocked && !editing && (
            <Button variant="outline" onClick={startEditing} className="gap-2">
              <Pencil className="h-4 w-4" />
              {t("pages.invoices.detail.editBtn", { defaultValue: "Editar" })}
            </Button>
          )}
          {isAdmin && (
            <Button
              variant="outline"
              onClick={() => setConfirmDelete(true)}
              className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-4 w-4" />
              {t("pages.invoices.detail.deleteBtn", { defaultValue: "Excluir" })}
            </Button>
          )}
        </div>
      </div>

      <InvoiceClientViewsCard invoiceId={invoice.id} />

      <EmailSendHistoryCard kind="invoice" invoiceId={invoice.id} />

      {editing && !isLocked && (
        <div className="rounded-lg border border-primary/40 bg-primary/5 p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold">
                {t("pages.invoices.detail.editingTitle", { defaultValue: "Editando fatura" })}
              </p>
              <p className="text-xs text-muted-foreground">
                {t("pages.invoices.detail.editingHelp", {
                  defaultValue: "As alterações são salvas apenas ao clicar em Salvar.",
                })}
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                onClick={cancelEditing}
                disabled={savingEdit}
                className="gap-2"
              >
                <X className="h-4 w-4" />
                {t("pages.invoices.detail.cancelBtn", { defaultValue: "Cancelar" })}
              </Button>
              <Button onClick={saveEditing} disabled={savingEdit} className="gap-2">
                {savingEdit ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {t("pages.invoices.detail.saveBtn", { defaultValue: "Salvar" })}
              </Button>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label>{t("pages.invoices.detail.editTitleLabel", { defaultValue: "Título" })}</Label>
              <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("pages.invoices.detail.fieldIssued")}</Label>
              <Input
                type="date"
                value={editIssueDate}
                onChange={(e) => setEditIssueDate(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t("pages.invoices.detail.fieldDue")}</Label>
              <Input
                type="date"
                value={editDueDate}
                onChange={(e) => setEditDueDate(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>
                {t("pages.invoices.detail.editTaxLabel", { defaultValue: "Imposto (%)" })}
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={editTaxPercent}
                onChange={(e) => setEditTaxPercent(Number(e.target.value) || 0)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>
                {t("pages.invoices.detail.editRetainageLabel", { defaultValue: "Retenção (%)" })}
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={editRetainagePercent}
                onChange={(e) => setEditRetainagePercent(Number(e.target.value) || 0)}
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>
                {t("pages.invoices.detail.editDescriptionLabel", { defaultValue: "Descrição" })}
              </Label>
              <Textarea
                rows={2}
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>{t("pages.invoices.detail.editNotesLabel", { defaultValue: "Notas" })}</Label>
              <Textarea rows={2} value={editNotes} onChange={(e) => setEditNotes(e.target.value)} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>{t("pages.estimates.form.scopePhotos")}</Label>
              <ScopePhotosUploader
                bucket="invoice-scope-photos"
                companyId={invoice.company_id}
                photos={editScopePhotos}
                setPhotos={setEditScopePhotos}
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>{t("pages.invoices.detail.lineItemsTitle")}</Label>
              <Button variant="outline" size="sm" onClick={addEditItem} className="gap-2">
                <Plus className="h-3.5 w-3.5" />
                {t("pages.invoices.detail.addItemBtn", { defaultValue: "Adicionar item" })}
              </Button>
            </div>
            <div className="space-y-2">
              {editItems.filter((it) => !it.toDelete).length === 0 && (
                <p className="text-xs text-muted-foreground">
                  {t("pages.invoices.detail.noItemsYet", {
                    defaultValue: "Nenhum item. Clique em Adicionar item.",
                  })}
                </p>
              )}
              {editItems.map((it) =>
                it.toDelete ? null : (
                  <div
                    key={it.id}
                    className="grid gap-2 rounded-md border border-border bg-background p-3 sm:grid-cols-[1fr_80px_120px_40px] sm:items-end"
                  >
                    <div className="space-y-1">
                      <Label className="text-xs">{t("pages.invoices.detail.descriptionCol")}</Label>
                      <Input
                        value={it.description}
                        onChange={(e) => updateEditItem(it.id, { description: e.target.value })}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">{t("pages.invoices.detail.qtyCol")}</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={it.quantity}
                        onChange={(e) =>
                          updateEditItem(it.id, { quantity: Number(e.target.value) || 0 })
                        }
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">{t("pages.invoices.detail.unitPriceCol")}</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={it.unit_price}
                        onChange={(e) =>
                          updateEditItem(it.id, { unit_price: Number(e.target.value) || 0 })
                        }
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeEditItem(it.id)}
                      className="self-end"
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                ),
              )}
            </div>
          </div>
        </div>
      )}

      <div className="rounded-lg border border-border bg-card">
        <div className="border-b border-border p-4">
          <p className="text-sm font-semibold">{t("pages.invoices.detail.lineItemsTitle")}</p>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("pages.invoices.detail.descriptionCol")}</TableHead>
                <TableHead className="w-20 text-right">
                  {t("pages.invoices.detail.qtyCol")}
                </TableHead>
                <TableHead className="w-32 text-right">
                  {t("pages.invoices.detail.unitPriceCol")}
                </TableHead>
                <TableHead className="w-32 text-right">
                  {t("pages.invoices.detail.totalCol")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((it) => (
                <TableRow key={it.id}>
                  <TableCell>{it.description}</TableCell>
                  <TableCell className="text-right">{it.quantity}</TableCell>
                  <TableCell className="text-right">{fm(it.unit_price)}</TableCell>
                  <TableCell className="text-right font-medium">{fm(it.total)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="flex flex-col items-end gap-1 border-t border-border p-4 text-sm">
          <div className="flex w-56 justify-between">
            <span className="text-muted-foreground">
              {t("pages.invoices.detail.subtotalLabel")}
            </span>
            <span>{fm(invoice.subtotal)}</span>
          </div>
          <div className="flex w-56 justify-between">
            <span className="text-muted-foreground">
              {t("pages.invoices.detail.taxLabel", { percent: invoice.tax_percent })}
            </span>
            <span>{fm(invoice.tax_amount)}</span>
          </div>
          {invoice.retainage_percent > 0 && (
            <div className="flex w-56 justify-between text-muted-foreground">
              <span>
                {t("pages.invoices.detail.retainageLabel", { percent: invoice.retainage_percent })}
              </span>
              <span>−{fm(invoice.retainage_amount)}</span>
            </div>
          )}
          <div className="flex w-56 justify-between border-t border-border pt-1 text-base font-semibold">
            <span>{t("pages.invoices.detail.totalLabel")}</span>
            <span>{fm(invoice.total)}</span>
          </div>
          <div className="flex w-56 justify-between text-emerald-600 dark:text-emerald-400">
            <span>{t("pages.invoices.detail.paidLabel")}</span>
            <span>{fm(invoice.amount_paid)}</span>
          </div>
          <div className="flex w-56 justify-between font-semibold">
            <span>{t("pages.invoices.detail.balanceDueLabel")}</span>
            <span>{fm(balance)}</span>
          </div>
          {contractSummary && (
            <div className="mt-2 w-56 space-y-1 border-t border-border pt-2">
              <div className="flex justify-between text-muted-foreground">
                <span>
                  {t("pages.invoices.detail.contractTotalLabel", {
                    defaultValue: "Total do contrato",
                  })}
                </span>
                <span>{fm(contractSummary.amount)}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>
                  {t("pages.invoices.detail.contractInvoicedLabel", { defaultValue: "Faturado" })}
                </span>
                <span>{fm(contractSummary.invoicedTotal)}</span>
              </div>
              <div className="flex justify-between text-emerald-600 dark:text-emerald-400">
                <span>
                  {t("pages.invoices.detail.contractPaidLabel", {
                    defaultValue: "Pago no contrato",
                  })}
                </span>
                <span>{fm(contractSummary.paidTotal)}</span>
              </div>
              <div className="flex justify-between font-semibold">
                <span>
                  {t("pages.invoices.detail.contractRemainingLabel", {
                    defaultValue: "Restante do contrato",
                  })}
                </span>
                <span>
                  {fm(
                    contractSummary.amount - contractSummary.paidTotal < 1
                      ? 0
                      : contractSummary.amount - contractSummary.paidTotal,
                  )}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border p-4">
          <p className="text-sm font-semibold">
            {t("pages.invoices.detail.paymentsTitle", { count: payments.length })}
          </p>
          {balance > 0 && (
            <Button variant="outline" size="sm" onClick={() => setPayOpen(true)} className="gap-2">
              <Plus className="h-3.5 w-3.5" />
              {t("pages.invoices.detail.addBtn")}
            </Button>
          )}
        </div>
        {payments.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            {t("pages.invoices.detail.noPaymentsYet")}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("pages.invoices.detail.colDate")}</TableHead>
                <TableHead>{t("pages.invoices.detail.colMethod")}</TableHead>
                <TableHead>{t("pages.invoices.detail.colReference")}</TableHead>
                <TableHead className="text-right">{t("pages.invoices.detail.colAmount")}</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>{new Date(p.payment_date).toLocaleDateString()}</TableCell>
                  <TableCell className="capitalize text-muted-foreground">
                    {t(`paymentMethods.${p.method}`)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{p.reference ?? "—"}</TableCell>
                  <TableCell className="text-right font-medium">{fm(p.amount)}</TableCell>
                  <TableCell>
                    {isAdmin && (
                      <Button variant="ghost" size="icon" onClick={() => deletePayment(p.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("pages.invoices.detail.recordPaymentTitle")}</DialogTitle>
            <DialogDescription>
              {t("pages.invoices.detail.balanceDuePrefix", { amount: fm(balance) })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t("pages.invoices.detail.amountLabel")}</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={payAmount}
                  onChange={(e) => setPayAmount(Number(e.target.value) || 0)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>{t("pages.invoices.detail.dateLabel")}</Label>
                <Input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{t("pages.invoices.detail.methodLabel")}</Label>
              <Select value={payMethod} onValueChange={(v) => setPayMethod(v as Method)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">{t("paymentMethods.bank_transfer")}</SelectItem>
                  <SelectItem value="card">{t("paymentMethods.card")}</SelectItem>
                  <SelectItem value="cash">{t("paymentMethods.cash")}</SelectItem>
                  <SelectItem value="check">{t("paymentMethods.check")}</SelectItem>
                  <SelectItem value="other">{t("paymentMethods.other")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("pages.invoices.detail.referenceOptional")}</Label>
              <Input
                value={payReference}
                onChange={(e) => setPayReference(e.target.value)}
                placeholder={t("pages.invoices.detail.referencePlaceholder")}
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t("pages.invoices.detail.notesOptional")}</Label>
              <Textarea rows={2} value={payNotes} onChange={(e) => setPayNotes(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)} disabled={recording}>
              {t("pages.invoices.detail.cancelBtn")}
            </Button>
            <Button onClick={recordPayment} disabled={recording}>
              {recording ? t("pages.invoices.detail.saving") : t("pages.invoices.detail.recordBtn")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {invoice.status === "draft" || invoice.status === "cancelled"
                ? t("pages.invoices.detail.deleteInvoiceTitle")
                : t("pages.invoices.detail.cancelInvoiceTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {invoice.status === "draft" || invoice.status === "cancelled" ? (
                <span
                  dangerouslySetInnerHTML={{
                    __html: t("pages.invoices.detail.deleteInvoiceDesc", {
                      number: invoice.number,
                    }),
                  }}
                />
              ) : (
                <Trans
                  i18nKey="pages.invoices.detail.cancelInvoiceDesc"
                  values={{ number: invoice.number }}
                  components={[<strong key="0" />, <strong key="1" />]}
                />
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("pages.invoices.detail.cancelBtn")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDeleteInvoice}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {invoice.status === "draft" || invoice.status === "cancelled"
                ? t("pages.invoices.detail.deleteBtn")
                : t("pages.invoices.detail.cancelInvoiceBtn")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={sendOpen} onOpenChange={(o) => !sending && setSendOpen(o)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("pages.invoices.detail.sendDialogTitle", {
                defaultValue: "Send invoice to client",
              })}
            </DialogTitle>
            <DialogDescription>
              {t("pages.invoices.detail.sendDialogDesc", {
                defaultValue:
                  "An email with a link to view the invoice will be sent to the recipient.",
              })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="send-invoice-email">
                {t("pages.invoices.detail.sendEmailLabel", { defaultValue: "Recipient email" })}
              </Label>
              <Input
                id="send-invoice-email"
                type="email"
                value={sendEmail}
                onChange={(e) => setSendEmail(e.target.value)}
                placeholder="client@example.com"
                disabled={sending}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="send-invoice-message">
                {t("pages.invoices.detail.sendMessageLabel", {
                  defaultValue: "Message (optional)",
                })}
              </Label>
              <Textarea
                id="send-invoice-message"
                value={sendMessage}
                onChange={(e) => setSendMessage(e.target.value)}
                placeholder={t("pages.invoices.detail.sendMessagePlaceholder", {
                  defaultValue: "Add a personal note to the email…",
                })}
                rows={4}
                maxLength={1000}
                disabled={sending}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)} disabled={sending}>
              {t("pages.invoices.detail.cancelBtn", { defaultValue: "Cancel" })}
            </Button>
            <Button
              variant="outline"
              onClick={openEmailPreview}
              disabled={sending || loadingPreview}
              className="gap-2"
            >
              {loadingPreview ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
              {t("pages.invoices.detail.previewEmailBtn", { defaultValue: "Visualizar email" })}
            </Button>
            <Button
              onClick={sendInvoice}
              disabled={
                sending || !sendEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(sendEmail.trim())
              }
            >
              {sending
                ? t("pages.invoices.detail.sendingBtn", { defaultValue: "Sending…" })
                : t("pages.invoices.detail.sendConfirmBtn", { defaultValue: "Send invoice" })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Paid-receipt send dialog (offered after a payment settles the invoice) */}
      <Dialog open={receiptOpen} onOpenChange={(o) => !sendingReceipt && setReceiptOpen(o)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("pages.invoices.detail.receiptDialogTitle", {
                defaultValue: "Send paid receipt to client",
              })}
            </DialogTitle>
            <DialogDescription>
              {t("pages.invoices.detail.receiptDialogDesc", {
                defaultValue:
                  "The invoice is fully paid. Send the client a PDF stamped PAID, in their language.",
              })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="receipt-email">
                {t("pages.invoices.detail.sendEmailLabel", { defaultValue: "Recipient email" })}
              </Label>
              <Input
                id="receipt-email"
                type="email"
                value={receiptEmail}
                onChange={(e) => setReceiptEmail(e.target.value)}
                placeholder="client@example.com"
                disabled={sendingReceipt}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="receipt-message">
                {t("pages.invoices.detail.sendMessageLabel", {
                  defaultValue: "Message (optional)",
                })}
              </Label>
              <Textarea
                id="receipt-message"
                value={receiptMessage}
                onChange={(e) => setReceiptMessage(e.target.value)}
                placeholder={t("pages.invoices.detail.sendMessagePlaceholder", {
                  defaultValue: "Add a personal note to the email…",
                })}
                rows={4}
                maxLength={1000}
                disabled={sendingReceipt}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReceiptOpen(false)}
              disabled={sendingReceipt}
            >
              {t("pages.invoices.detail.receiptSkipBtn", { defaultValue: "Not now" })}
            </Button>
            <Button
              onClick={sendReceipt}
              disabled={
                sendingReceipt ||
                !receiptEmail.trim() ||
                !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(receiptEmail.trim())
              }
            >
              {sendingReceipt
                ? t("pages.invoices.detail.sendingBtn", { defaultValue: "Sending…" })
                : t("pages.invoices.detail.receiptSendBtn", { defaultValue: "Send receipt" })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={emailPreviewOpen} onOpenChange={setEmailPreviewOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {t("pages.invoices.detail.emailPreviewTitle", {
                defaultValue: "Como o cliente recebe",
              })}
            </DialogTitle>
            <DialogDescription>
              {t("pages.invoices.detail.emailPreviewDesc", {
                defaultValue: "Pré-visualização do email que será enviado.",
              })}
            </DialogDescription>
          </DialogHeader>
          {emailPreviewMeta && (
            <div className="space-y-1 rounded-md border border-border bg-muted/40 p-3 text-xs">
              <div>
                <span className="font-medium">De:</span> {emailPreviewMeta.from}
              </div>
              <div>
                <span className="font-medium">Para:</span> {emailPreviewMeta.recipient || "—"}
              </div>
              <div>
                <span className="font-medium">Assunto:</span> {emailPreviewMeta.subject}
              </div>
            </div>
          )}
          <div className="relative h-[60vh] overflow-hidden rounded-md border border-border bg-white">
            {emailPreviewUrl && (
              <iframe
                title="email-preview"
                src={emailPreviewUrl}
                className="pointer-events-none h-full w-full"
              />
            )}
            <div className="pointer-events-none absolute right-2 top-2 rounded bg-black/60 px-2 py-0.5 text-[10px] font-medium text-white">
              {t("pages.invoices.detail.previewOnly", {
                defaultValue: "Pré-visualização — links desativados",
              })}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailPreviewOpen(false)}>
              {t("pages.invoices.detail.closeBtn", { defaultValue: "Fechar" })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={pendingStatus !== null}
        onOpenChange={(open) => !open && setPendingStatus(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-muted-foreground" />
              {t("pages.invoices.detail.lockConfirmTitle", {
                status: pendingStatus ? t(`statuses.${pendingStatus}`) : "",
              })}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p
                  dangerouslySetInnerHTML={{ __html: t("pages.invoices.detail.lockConfirmLine1") }}
                />
                <p>{t("pages.invoices.detail.lockConfirmLine2")}</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("pages.invoices.detail.cancelBtn")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingStatus) {
                  const s = pendingStatus;
                  setPendingStatus(null);
                  updateStatus(s);
                }
              }}
            >
              {t("pages.invoices.detail.yesLockBtn")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmRelease} onOpenChange={setConfirmRelease}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Unlock className="h-5 w-5 text-muted-foreground" />
              {t("pages.invoices.detail.releaseConfirmTitle", {
                amount: fm(invoice.retainage_amount),
              })}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p
                  dangerouslySetInnerHTML={{
                    __html: t("pages.invoices.detail.releaseConfirmLine1", {
                      amount: fm(invoice.retainage_amount),
                      number: invoice.number,
                    }),
                  }}
                />
                <p>{t("pages.invoices.detail.releaseConfirmLine2")}</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={releasing}>
              {t("pages.invoices.detail.cancelBtn")}
            </AlertDialogCancel>
            <AlertDialogAction onClick={releaseRetainage} disabled={releasing}>
              {releasing
                ? t("pages.invoices.detail.creating")
                : t("pages.invoices.detail.createReleaseBtn")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={pdfPreviewOpen}
        onOpenChange={(open) => (open ? setPdfPreviewOpen(true) : closePdfPreview())}
      >
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>{t("pages.invoices.detail.pdfPreviewTitle")}</DialogTitle>
            <DialogDescription>
              {t("pages.invoices.detail.pdfPreviewDesc", { filename: pdfPreviewFilename })}
            </DialogDescription>
          </DialogHeader>
          <div className="h-[70vh] w-full overflow-hidden rounded-md border border-border bg-muted">
            {pdfPreviewBase64 ? (
              <PdfCanvasPreview pdfBase64={pdfPreviewBase64} />
            ) : (
              <div className="flex h-full items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closePdfPreview}>
              {t("pages.invoices.detail.closeBtn")}
            </Button>
            <Button onClick={confirmDownloadPdf} disabled={!pdfPreviewBase64} className="gap-2">
              <FileDown className="h-4 w-4" />
              {t("pages.invoices.detail.downloadPdfBtn")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <p className="mt-6 text-center text-[11px] italic text-muted-foreground">
        {t("pages.invoices.detail.footerNote")}
      </p>
    </div>
  );
}

function Field({
  label,
  value,
  sub,
  icon,
}: {
  label: string;
  value: string;
  sub?: string;
  icon?: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-2">
      {icon}
      <div>
        <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className="font-medium">{value}</p>
        {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
      </div>
    </div>
  );
}
